import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import {
  Button,
  FormControl,
  TableCell,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  MenuItem,
  makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Edit as EditIcon, Delete as DeleteIcon } from "@material-ui/icons";
import axios from "axios";
import { connect } from "redux-zero/react";
import actions from "../../actions";
import ConfirmDialog from "../ConfirmDialog";
import { accountLabel } from "../../utils";

const useStyles = makeStyles((theme) => ({
  formControlFullWidth: {
    margin: theme.spacing(1, 0),
    width: "100%",
  },
  actionButton: {
    cursor: "pointer",
  },
  deleteEntry: {
    cursor: "pointer",
    marginTop: 13,
  },
  section: {
    marginTop: theme.spacing(2),
  },
  columnHeader: {
    fontSize: 12,
    textAlign: "center",
  },
  entryCheckbox: {
    textAlign: "center",
  },
}));

const templateTypes = [
  { value: "nto1", label: "Multiple Debits to Single Credit" },
  { value: "nton", label: "Debit/Credit Pairs" },
];

const blankJson = {
  nto1: () => ({ reserveFundAccountCode: "", debit: [], narration: "" }),
  nton: () => ({ entries: [], narration: "", reverseApplicable: false }),
};

// Value account code used in Debit/Credit Pairs when the amount is provided directly
const provideValueCode = 0;
const provideValueOption = { code: provideValueCode, title: "(Provide Value)" };

const blankFormValue = () => ({
  id: 0,
  name: "",
  type: "nto1",
  json: blankJson.nto1(),
});

function AccountPicker({ accounts, label, code, onChange }) {
  const classes = useStyles();
  const value = accounts.find((o) => o.code === code) || { code: "", title: "" };
  return (
    <FormControl className={classes.formControlFullWidth}>
      <Autocomplete
        value={value}
        options={accounts}
        getOptionLabel={accountLabel}
        selectOnFocus
        disableOpenOnFocus
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            label={label}
          />
        )}
        onChange={(_, value) => onChange(value ? value.code : "")}
      />
    </FormControl>
  );
}

function AccountText({ accounts, code }) {
  const classes = useStyles();
  const account =
    code === provideValueCode
      ? provideValueOption
      : accounts.find((o) => o.code === code);
  return (
    <FormControl className={classes.formControlFullWidth}>
      {account ? accountLabel(account) : code}
    </FormControl>
  );
}

const mapToProps = ({ selectedSociety }) => ({ selectedSociety });

function JVTemplates({ selectedSociety }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const isAdmin = window.decodedJwt.role === "Administrator";

  const [formKey, setFormKey] = useState(1);
  const [errors, setErrors] = useState([]);
  const [mode, setMode] = useState("Create");
  const [model, setModel] = useState(blankFormValue());
  const [accounts, setAccounts] = useState([]);

  const [templates, setTemplates] = useState([]);
  const [refreshTemplates, setRefreshTemplates] = useState(1);

  const [deleteModel, setDeleteModel] = useState();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const blankNto1Entry = { accountCode: "", valueAccountCode: "", amount: "" };
  const blankNtonEntry = {
    debitAccountCode: "",
    creditAccountCode: "",
    valueAccountCode: "",
    amount: "",
    excludeFromReverse: false,
  };
  const [newNto1Entry, setNewNto1Entry] = useState(blankNto1Entry);
  const [newNtonEntry, setNewNtonEntry] = useState(blankNtonEntry);

  useEffect(() => {
    axios
      .get("/account/selectitems/all", {
        headers: { "X-SocietyId": selectedSociety },
      })
      .then((response) => {
        setAccounts(response.data);
      });
  }, [selectedSociety]);

  useEffect(() => {
    axios
      .get("/transactiontemplate/list", {
        headers: { "X-SocietyId": selectedSociety },
      })
      .then((response) => {
        setTemplates(response.data);
      });
  }, [selectedSociety, refreshTemplates]);

  function apiErrors(error) {
    const data = error.response && error.response.data;
    if (!data) return ["Error: " + error.message];
    const details = (data.details || []).map((o) => o.message);
    return ["Error: " + data.message, ...details];
  }

  function setJson(json) {
    setModel({ ...model, json: { ...model.json, ...json } });
  }

  function resetForm() {
    setModel(blankFormValue());
    setNewNto1Entry(blankNto1Entry);
    setNewNtonEntry(blankNtonEntry);
    setErrors([]);
    setMode("Create");
    setFormKey(formKey + 1);
  }

  function changeType(type) {
    if (type === model.type) return;
    const hasEntries =
      model.type === "nto1"
        ? model.json.debit.length > 0
        : model.json.entries.length > 0;
    if (
      hasEntries &&
      !window.confirm("Changing the type will clear the entries. Continue?")
    )
      return;
    setModel({
      ...model,
      type,
      json: { ...blankJson[type](), narration: model.json.narration },
    });
  }

  function addNto1Entry() {
    if (!newNto1Entry.accountCode || newNto1Entry.valueAccountCode === "") {
      alert("Debit Account and Value Account are required!");
      return;
    }
    const provideValue = newNto1Entry.valueAccountCode === provideValueCode;
    const amount = Number(newNto1Entry.amount);
    if (provideValue && !(amount > 0)) {
      alert("Amount must be greater than zero!");
      return;
    }
    const entry = { ...newNto1Entry, amount: provideValue ? amount : null };
    setJson({ debit: [...model.json.debit, entry] });
    setNewNto1Entry(blankNto1Entry);
    setFormKey(formKey + 1);
  }

  function addNtonEntry() {
    if (
      !newNtonEntry.debitAccountCode ||
      !newNtonEntry.creditAccountCode ||
      newNtonEntry.valueAccountCode === ""
    ) {
      alert("Debit Account, Credit Account and Value Account are required!");
      return;
    }
    const provideValue = newNtonEntry.valueAccountCode === provideValueCode;
    const amount = Number(newNtonEntry.amount);
    if (provideValue && !(amount > 0)) {
      alert("Amount must be greater than zero!");
      return;
    }
    const entry = { ...newNtonEntry, amount: provideValue ? amount : null };
    setJson({ entries: [...model.json.entries, entry] });
    setNewNtonEntry(blankNtonEntry);
    setFormKey(formKey + 1);
  }

  function updateEntryAmount(listName, index, amount) {
    setJson({
      [listName]: model.json[listName].map((o, i) =>
        i === index ? { ...o, amount } : o
      ),
    });
  }

  function updateEntryExcludeFromReverse(index, excludeFromReverse) {
    setJson({
      entries: model.json.entries.map((o, i) =>
        i === index ? { ...o, excludeFromReverse } : o
      ),
    });
  }

  function deleteEntry(listName, index) {
    setJson({ [listName]: model.json[listName].filter((_, i) => i !== index) });
  }

  function save() {
    let newErrors = [];
    if (!model.name.trim()) newErrors.push("Name is required");
    if (model.type === "nto1") {
      if (!model.json.reserveFundAccountCode)
        newErrors.push("Credit Account is required");
      if (model.json.debit.length === 0)
        newErrors.push("At least one Debit entry is required");
    } else {
      if (model.json.entries.length === 0)
        newErrors.push("At least one entry is required");
    }
    const entries =
      model.type === "nto1" ? model.json.debit : model.json.entries;
    if (
      entries.some(
        (o) => o.valueAccountCode === provideValueCode && !(Number(o.amount) > 0)
      )
    )
      newErrors.push("Amount must be greater than zero");
    if (
      templates.some(
        (o) =>
          o.id !== model.id &&
          o.name.trim().toLowerCase() === model.name.trim().toLowerCase()
      )
    )
      newErrors.push("A template with this name already exists");

    setErrors(newErrors);
    if (newErrors.length > 0) return;

    const listName = model.type === "nto1" ? "debit" : "entries";
    const value = {
      ...model,
      name: model.name.trim(),
      json: {
        ...model.json,
        [listName]: entries.map((o) =>
          o.valueAccountCode === provideValueCode
            ? { ...o, amount: Number(o.amount) }
            : o
        ),
      },
    };
    axios({
      method: mode === "Create" ? "POST" : "PUT",
      url:
        mode === "Create"
          ? "/transactiontemplate"
          : "/transactiontemplate/" + model.id,
      headers: { "X-SocietyId": selectedSociety },
      data: JSON.stringify(value),
    })
      .then((response) => {
        enqueueSnackbar("Saved", { variant: "success" });
        resetForm();
        setRefreshTemplates(refreshTemplates + 1);
      })
      .catch((error) => {
        setErrors(apiErrors(error));
      });
  }

  function edit(row) {
    setModel(JSON.parse(JSON.stringify(row)));
    setNewNto1Entry(blankNto1Entry);
    setNewNtonEntry(blankNtonEntry);
    setErrors([]);
    setMode("Edit");
    setFormKey(formKey + 1);
  }

  function ddelete(row) {
    setDeleteModel(row);
    setShowConfirmDelete(true);
  }

  function closeConfirmDeleteDialog() {
    setShowConfirmDelete(false);
  }

  function deleteTemplate(row) {
    closeConfirmDeleteDialog();
    axios({
      method: "DELETE",
      url: "/transactiontemplate/" + row.id,
      headers: { "X-SocietyId": selectedSociety },
    })
      .then((response) => {
        if (model.id === row.id) resetForm();
        setRefreshTemplates(refreshTemplates + 1);
      })
      .catch((error) => {
        alert(apiErrors(error).join("\n"));
      });
  }

  function renderNto1() {
    return (
      <>
        <div className={classes.section}>
          <h4>Credit</h4>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <AccountPicker
                accounts={accounts}
                label="Credit Account"
                code={model.json.reserveFundAccountCode}
                onChange={(code) => setJson({ reserveFundAccountCode: code })}
              />
            </Grid>
          </Grid>
        </div>
        <div className={classes.section}>
          <h4>Debits</h4>
          {model.json.debit.map((entry, index) => (
            <Grid key={index} container spacing={2}>
              <Grid item xs={5}>
                <AccountText accounts={accounts} code={entry.accountCode} />
              </Grid>
              <Grid item xs={4}>
                <AccountText accounts={accounts} code={entry.valueAccountCode} />
              </Grid>
              <Grid item xs={2}>
                {entry.valueAccountCode === provideValueCode && (
                  <FormControl className={classes.formControlFullWidth}>
                    <TextField
                      type="number"
                      variant="outlined"
                      size="small"
                      value={entry.amount == null ? "" : entry.amount}
                      onChange={(e) =>
                        updateEntryAmount("debit", index, e.target.value)
                      }
                    />
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={1}>
                <DeleteIcon
                  className={classes.deleteEntry}
                  onClick={() => deleteEntry("debit", index)}
                />
              </Grid>
            </Grid>
          ))}
          <Grid container spacing={2}>
            <Grid item xs={5}>
              <AccountPicker
                accounts={accounts}
                label="Debit Account"
                code={newNto1Entry.accountCode}
                onChange={(code) =>
                  setNewNto1Entry({ ...newNto1Entry, accountCode: code })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <AccountPicker
                accounts={[provideValueOption, ...accounts]}
                label="Value Account"
                code={newNto1Entry.valueAccountCode}
                onChange={(code) =>
                  setNewNto1Entry({
                    ...newNto1Entry,
                    valueAccountCode: code,
                    amount: code === provideValueCode ? newNto1Entry.amount : "",
                  })
                }
              />
            </Grid>
            <Grid item xs={2}>
              {newNto1Entry.valueAccountCode === provideValueCode && (
                <FormControl className={classes.formControlFullWidth}>
                  <TextField
                    label="Amount"
                    type="number"
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={newNto1Entry.amount}
                    onChange={(e) =>
                      setNewNto1Entry({ ...newNto1Entry, amount: e.target.value })
                    }
                  />
                </FormControl>
              )}
            </Grid>
            <Grid item xs={1}>
              <Button
                className={classes.deleteEntry}
                size="small"
                onClick={addNto1Entry}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </div>
      </>
    );
  }

  function renderNton() {
    const reverseApplicable = !!model.json.reverseApplicable;
    return (
      <div className={classes.section}>
        <FormControlLabel
          control={
            <Checkbox
              checked={reverseApplicable}
              onChange={(e) => setJson({ reverseApplicable: e.target.checked })}
            />
          }
          label="Reverse Applicable"
        />
        <h4>Entries</h4>
        {reverseApplicable && (
          <Grid container spacing={2}>
            <Grid item xs={10}></Grid>
            <Grid item xs={1} className={classes.columnHeader}>
              Exclude from Reverse
            </Grid>
          </Grid>
        )}
        {model.json.entries.map((entry, index) => (
          <Grid key={index} container spacing={2}>
            <Grid item xs={3}>
              <AccountText accounts={accounts} code={entry.debitAccountCode} />
            </Grid>
            <Grid item xs={3}>
              <AccountText accounts={accounts} code={entry.creditAccountCode} />
            </Grid>
            <Grid item xs={reverseApplicable ? 2 : 3}>
              <AccountText accounts={accounts} code={entry.valueAccountCode} />
            </Grid>
            <Grid item xs={2}>
              {entry.valueAccountCode === provideValueCode && (
                <FormControl className={classes.formControlFullWidth}>
                  <TextField
                    type="number"
                    variant="outlined"
                    size="small"
                    value={entry.amount == null ? "" : entry.amount}
                    onChange={(e) =>
                      updateEntryAmount("entries", index, e.target.value)
                    }
                  />
                </FormControl>
              )}
            </Grid>
            {reverseApplicable && (
              <Grid item xs={1} className={classes.entryCheckbox}>
                <Checkbox
                  size="small"
                  checked={!!entry.excludeFromReverse}
                  onChange={(e) =>
                    updateEntryExcludeFromReverse(index, e.target.checked)
                  }
                />
              </Grid>
            )}
            <Grid item xs={1}>
              <DeleteIcon
                className={classes.deleteEntry}
                onClick={() => deleteEntry("entries", index)}
              />
            </Grid>
          </Grid>
        ))}
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <AccountPicker
              accounts={accounts}
              label="Debit Account"
              code={newNtonEntry.debitAccountCode}
              onChange={(code) =>
                setNewNtonEntry({ ...newNtonEntry, debitAccountCode: code })
              }
            />
          </Grid>
          <Grid item xs={3}>
            <AccountPicker
              accounts={accounts}
              label="Credit Account"
              code={newNtonEntry.creditAccountCode}
              onChange={(code) =>
                setNewNtonEntry({ ...newNtonEntry, creditAccountCode: code })
              }
            />
          </Grid>
          <Grid item xs={reverseApplicable ? 2 : 3}>
            <AccountPicker
              accounts={[provideValueOption, ...accounts]}
              label="Value Account"
              code={newNtonEntry.valueAccountCode}
              onChange={(code) =>
                setNewNtonEntry({
                  ...newNtonEntry,
                  valueAccountCode: code,
                  amount: code === provideValueCode ? newNtonEntry.amount : "",
                })
              }
            />
          </Grid>
          <Grid item xs={2}>
            {newNtonEntry.valueAccountCode === provideValueCode && (
              <FormControl className={classes.formControlFullWidth}>
                <TextField
                  label="Amount"
                  type="number"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={newNtonEntry.amount}
                  onChange={(e) =>
                    setNewNtonEntry({ ...newNtonEntry, amount: e.target.value })
                  }
                />
              </FormControl>
            )}
          </Grid>
          {reverseApplicable && (
            <Grid item xs={1} className={classes.entryCheckbox}>
              <Checkbox
                size="small"
                checked={newNtonEntry.excludeFromReverse}
                onChange={(e) =>
                  setNewNtonEntry({
                    ...newNtonEntry,
                    excludeFromReverse: e.target.checked,
                  })
                }
              />
            </Grid>
          )}
          <Grid item xs={1}>
            <Button
              className={classes.deleteEntry}
              size="small"
              onClick={addNtonEntry}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <>
      {isAdmin && (
        <form key={formKey} autoComplete="off">
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl className={classes.formControlFullWidth}>
                <TextField
                  label="Name"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={model.name}
                  onChange={(e) => setModel({ ...model, name: e.target.value })}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl className={classes.formControlFullWidth}>
                <TextField
                  select
                  label="Type"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={model.type}
                  onChange={(e) => changeType(e.target.value)}
                >
                  {templateTypes.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Grid>
          </Grid>

          {model.type === "nto1" ? renderNto1() : renderNton()}

          <div className={classes.section}>
            <FormControl className={classes.formControlFullWidth}>
              <TextField
                label="Narration"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={model.json.narration}
                onChange={(e) => setJson({ narration: e.target.value })}
              />
            </FormControl>
          </div>
          <div>
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </div>
          <div>
            <Button
              variant="contained"
              size="small"
              color="primary"
              type="button"
              onClick={save}
            >
              Save
            </Button>{" "}
            {mode === "Edit" && (
              <Button size="small" type="button" onClick={resetForm}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      )}

      <TableContainer component={Paper} className={classes.section}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Entries</TableCell>
              <TableCell>Narration</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((row) => (
              <TableRow hover key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>
                  {templateTypes.find((o) => o.value === row.type).label}
                </TableCell>
                <TableCell>
                  {row.type === "nto1"
                    ? row.json.debit.length
                    : row.json.entries.length}
                </TableCell>
                <TableCell>{row.json.narration}</TableCell>
                {isAdmin && (
                  <TableCell>
                    <EditIcon
                      className={classes.actionButton}
                      onClick={() => edit(row)}
                    />
                    <DeleteIcon
                      className={classes.actionButton}
                      onClick={() => ddelete(row)}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        model={deleteModel}
        show={showConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this template?"
        onClose={closeConfirmDeleteDialog}
        onYes={deleteTemplate}
      />
    </>
  );
}

export default connect(mapToProps, actions)(JVTemplates);
